import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, ConfirmDialog } from '@mlm/ui';
import { dropDlqJob, listDlqJobs, retryDlqJob, type DlqJob } from '../lib/api';

/**
 * Phase 12D — Dead-letter queue admin. Lists failed BullMQ jobs across the
 * comp-event queues; admin can retry or drop. Idempotent: retry re-enqueues
 * with the same jobId so `comp_plan_runs` uniqueness gates re-runs.
 */
export function DlqPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dlq'], queryFn: listDlqJobs });
  const [opened, setOpened] = useState<string | null>(null);
  const [dropping, setDropping] = useState<{ queue: string; jobId: string } | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);

  const retryMut = useMutation({
    mutationFn: ({ queue, jobId }: { queue: string; jobId: string }) => retryDlqJob(queue, jobId),
    meta: { successMessage: 'Job retried.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dlq'] }),
  });
  const dropMut = useMutation({
    mutationFn: ({ queue, jobId }: { queue: string; jobId: string }) => dropDlqJob(queue, jobId),
    meta: { successMessage: 'Job dropped.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dlq'] });
      setDropping(null);
    },
  });

  async function handleConfirmDrop() {
    if (!dropping) return;
    setDropError(null);
    try {
      await dropMut.mutateAsync(dropping);
    } catch (err) {
      setDropError(err instanceof Error ? err.message : 'Failed to drop job.');
    }
  }

  const key = (j: DlqJob) => `${j.queue}|${j.jobId}`;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Dead-letter queue</h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Failed BullMQ jobs across the comp-event queues. Retry re-enqueues with the same job id
          (idempotent via <code className="font-mono">comp_plan_runs</code> uniqueness). Drop
          removes the job from the failed list without retrying.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !q.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No failed jobs. Worker is clean.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium w-8"></th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Queue</th>
                  <th className="px-4 py-2 font-medium">Job id</th>
                  <th className="px-4 py-2 font-medium">Attempts</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((j) => {
                  const k = key(j);
                  const isOpen = opened === k;
                  return (
                    <>
                      <tr
                        key={k}
                        className="border-b last:border-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => setOpened(isOpen ? null : k)}
                      >
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {isOpen ? '▾' : '▸'}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {new Date(j.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{j.queue}</td>
                        <td className="px-4 py-2 font-mono text-xs">{j.jobId.slice(0, 24)}</td>
                        <td className="px-4 py-2 text-xs">{j.attemptsMade}</td>
                        <td className="px-4 py-2 text-xs text-destructive truncate max-w-xs">
                          {j.failedReason}
                        </td>
                        <td
                          className="px-4 py-2 text-right space-x-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retryMut.isPending}
                            onClick={() => retryMut.mutate({ queue: j.queue, jobId: j.jobId })}
                          >
                            Retry
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={dropMut.isPending}
                            onClick={() => {
                              setDropError(null);
                              setDropping({ queue: j.queue, jobId: j.jobId });
                            }}
                          >
                            Drop
                          </Button>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr key={`${k}-detail`} className="bg-muted/20 border-b">
                          <td colSpan={7} className="px-8 py-3">
                            <p className="text-xs font-medium mb-1">Payload</p>
                            <pre className="text-[10px] font-mono bg-background border rounded p-2 overflow-x-auto">
                              {JSON.stringify(j.data, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ) : null}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={dropping !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDropping(null);
            setDropError(null);
          }
        }}
        title="Drop this job?"
        description={
          dropping ? (
            <span>
              Removes <span className="font-mono">{dropping.jobId.slice(0, 24)}</span> from queue{' '}
              <span className="font-mono">{dropping.queue}</span>. The job stops retrying. Cannot be
              undone.
            </span>
          ) : (
            'Removes the job from the failed list. Cannot be undone.'
          )
        }
        confirmLabel="Drop job"
        confirmVariant="destructive"
        loading={dropMut.isPending}
        errorMessage={dropError}
        onConfirm={handleConfirmDrop}
      />
    </div>
  );
}
